<template>
  <transition
    name="sf-fade"
    @before-enter="setInitialStyles"
    @enter="onEnter"
    @before-leave="setInitialStyles"
    @after-enter="onAfterEnter"
    @leave="onLeave"
  >
    <slot />
  </transition>
</template>

<script>
export default {
  name: 'Fade',

  props: {
    speed: {
      type: String,
    },
    slide: {
      type: String,
    },
    isFloating: {
      type: Boolean,
      default: false,
    },
    isLeaveTo: {
      type: Boolean,
      default: true,
    },
    isEnterFrom: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    duration() {
      switch (this.speed) {
        case 'fast':
          return '75ms linear'
        case 'slow':
          return '.25s ease-in-out'
        case 'slower':
          return '.45s ease-in-out'
      }
      return '.15s ease-in-out'
    },
    transform() {
      switch (this.slide) {
        case 'down':
          return 'translate3d(0, -100%, 0)'
        case 'left':
          return 'translate3d(-100%, 0, 0)'
        case 'right':
          return 'translate3d(100%, 0, 0)'
        case 'up':
          return 'translate3d(0, 100%, 0)'
        default:
          return 'none'
      }
    },
    opacity() {
      return this.transform !== 'none' ? 0.1 : 0
    },
    enterFromOpacity() {
      return this.isEnterFrom ? this.opacity : 1
    },
    leaveToOpacity() {
      return this.isLeaveTo ? this.opacity : 1
    },
    enterFromTransform() {
      return this.isEnterFrom ? this.transform : 'none'
    },
    leaveToTransform() {
      return this.isLeaveTo ? this.transform : 'none'
    },
    isSlideY() {
      return this.slide === 'down' || this.slide === 'up'
    },
    height() {
      if (this.isFloating) return null
      return this.isSlideY ? 0 : 'auto'
    },
    width() {
      if (this.isFloating) return null
      return this.slide && !this.isSlideY ? 0 : 'auto'
    },
    enterFromHeight() {
      return this.isEnterFrom ? this.height : 'auto'
    },
    enterFromWidth() {
      return this.isEnterFrom ? this.width : 'auto'
    },
    willChange() {
      let properties = 'opacity'
      if (this.transform !== 'none') properties += ', transform'
      if (this.height === 0) properties += ', height'
      if (this.width === 0) properties += ', width'
      return properties
    },
  },

  methods: {
    setInitialStyles(el) {
      el.style.setProperty('--fade-will-change', this.willChange)
      el.style.setProperty('--fade-duration', this.duration)
      el.style.setProperty(
        '--fade-enter-opacity',
        String(this.enterFromOpacity)
      )
      el.style.setProperty(
        '--fade-enter-transform',
        String(this.enterFromTransform)
      )
      el.style.setProperty('--fade-enter-height', String(this.enterFromHeight))
      el.style.setProperty('--fade-enter-width', String(this.enterFromWidth))
      el.style.setProperty('--fade-leave-opacity', String(this.leaveToOpacity))
      el.style.setProperty(
        '--fade-leave-transform',
        String(this.leaveToTransform)
      )
    },
    onEnter(el) {
      if (this.slide && !this.isFloating && this.isEnterFrom) {
        if (this.isSlideY) {
          el.style.width = getComputedStyle(el).width
          el.style.height = 'auto'
        } else {
          el.style.width = 'auto'
          el.style.height = getComputedStyle(el).height
        }
        el.style.position = 'absolute'
        el.style.visibility = 'hidden'
        const computed = getComputedStyle(el)
        const width = computed.width
        const height = computed.height
        el.style.width = this.isSlideY ? '' : '0'
        el.style.height = this.isSlideY ? '0' : ''
        el.style.position = ''
        el.style.visibility = ''
        // Force repaint to make sure the animation is triggered correctly
        // eslint-disable-next-line no-unused-expressions
        getComputedStyle(el)[this.isSlideY ? 'height' : 'width']
        requestAnimationFrame(() => {
          if (this.isSlideY) {
            el.style.height = height
          } else {
            el.style.width = width
          }
        })
      }
    },
    onAfterEnter(el) {
      if (this.slide && !this.isFloating && this.isEnterFrom) {
        el.style[this.isSlideY ? 'height' : 'width'] = ''
      }
    },
    onLeave(el) {
      if (this.slide && !this.isFloating && this.isLeaveTo) {
        if (this.isSlideY) {
          el.style.height = getComputedStyle(el).height
        } else {
          el.style.width = getComputedStyle(el).width
        }
        const sizeProp = this.isSlideY ? 'height' : 'width'
        // Force repaint
        // eslint-disable-next-line no-unused-expressions
        getComputedStyle(el)[sizeProp]
        requestAnimationFrame(() => {
          el.style[sizeProp] = '0'
        })
      }
    },
  },
}
</script>

<style>
[class*='sf-fade-'] {
  will-change: var(--fade-will-change);
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}
.sf-fade-enter-active,
.sf-fade-leave-active {
  transition: opacity var(--fade-duration), transform var(--fade-duration),
    height var(--fade-duration), width var(--fade-duration);
  overflow: hidden;
}
.sf-fade-enter {
  opacity: var(--fade-enter-opacity);
  transform: var(--fade-enter-transform);
  height: var(--fade-enter-height);
  width: var(--fade-enter-width);
}
.sf-fade-leave-to {
  opacity: var(--fade-leave-opacity);
  transform: var(--fade-leave-transform);
}
</style>
